import { useLang } from 'hooks/lang';

import { actions } from 'entities';

import {
  LabeledSelect,
  LabeledInput,
  Button,
  ResponsiveOverlay,
  RadioInput,
  Column,
  Label,
  Card,
} from 'components/Blocks';
import { FolderTreeIcon } from 'components/Icons';

import { useProjectProfile } from 'pages/Projects/ProjectProfile/context';
import { useFormControl } from 'hooks';

import { COUNTRIES } from 'utils/countries';
import { DEVICE_OPTIONS, SAMPLE_HOST_OPTIONS } from 'utils/constants';

import { TaskOptionOverlayProps } from '../createTaskOptions';
import { getFirst, listGet } from 'utils/array';

export function CreateSampleProjectModal({
  task,
  updateTask,
  isOpen,
  close,
}: TaskOptionOverlayProps) {
  const { project } = useProjectProfile();

  const projectDeviceTarget = DEVICE_OPTIONS.find(({ id }) => project?.deviceTarget === id);

  const countries = COUNTRIES.filter(
    ({ sampleId, value, externalPanel }) =>
      !externalPanel && sampleId && project.countries.includes(value),
  );

  const canChooseCountry = countries.length > 1;

  const values = useFormControl({
    clientPm: '',
    emailSubject: '',
    countries: canChooseCountry ? [] : [getFirst(countries).sampleId!],
    deviceTarget: projectDeviceTarget?.id ?? '',
    host: '',
  });

  const { t } = useLang();

  const createProject = actions.projects.createSampleProject.use({
    params: {
      project: task.projectId,
      task: task.id,
      ...values.state,
    },

    onSuccess: ({ sampleId }) => {
      updateTask({
        id: task?.id as string,
        sampleProjectIds: [...(task?.sampleProjectIds ?? []), sampleId],
      });

      values.resetDefault();
      close();
    },
  });

  const valuesOk = Object.values(values.state).every((value) =>
    Array.isArray(value) ? value.length : value,
  );

  return (
    <>
      <ResponsiveOverlay
        isOpen={isOpen}
        close={close}
        title={t('create-project-on-sample')}
        width="min(90vw, 740px)"
        gap="6"
        className="overflow-auto"
      >
        <LabeledInput defaultValue={project.name} disabled label={t('name')} />

        <LabeledInput
          label={t('client-pm-email')}
          value={values.state.clientPm}
          onChange={values.handleChange}
          name="clientPm"
          placeholder={t('email')!}
        />

        <LabeledInput
          label={t('email-subject')}
          value={values.state.emailSubject}
          onChange={values.handleChange}
          name="emailSubject"
          autoComplete="off"
        />

        {canChooseCountry ? (
          <LabeledSelect
            label={t('countries')}
            value={values.state.countries}
            onChangeDirect={(countries) => values.dispatch({ countries })}
            valueKey="sampleId"
            options={countries}
            isMulti
          />
        ) : (
          <Column gap="2">
            <Label>{t('countries')}</Label>

            <Card spacing="small" className="shadow-none">
              {listGet(countries, getFirst(values.state.countries), 'sampleId')?.label}
            </Card>

            <span className="text-xs italic">{t('only-country-available')}</span>
          </Column>
        )}

        <Column gap="2">
          <Label>{t('who-will-host')}</Label>

          <RadioInput
            orientation="horizontal"
            className="w-full shrink rounded-md border py-1 text-sm transition-colors hover:bg-hover"
            selectedClassName="border-primary text-primary"
            addDot={false}
            value={values.state.host}
            onChangeDirect={(host) => values.dispatch({ host })}
            options={SAMPLE_HOST_OPTIONS.map(({ getLabel, sampleId }) => ({
              value: sampleId,
              label: getLabel(t),
            }))}
          />
        </Column>

        <Column gap="2">
          <Label>{t('device-target')}</Label>

          <RadioInput
            orientation="horizontal"
            className="w-full shrink rounded-md border py-2 text-sm transition-colors hover:bg-hover"
            selectedClassName="border-primary text-primary"
            alignItems="stretch"
            addDot={false}
            value={values.state.deviceTarget}
            onChangeDirect={(deviceTarget) => values.dispatch({ deviceTarget })}
            options={DEVICE_OPTIONS.map(({ getLabel, sampleId, icon }) => ({
              value: sampleId,
              label: getLabel(t),
              Icon: icon,
            }))}
            render={({ Icon, label }) => (
              <Column gap="2" alignItems="center">
                <Icon size={24} />
                <span>{label}</span>
              </Column>
            )}
          />
        </Column>

        <Button {...createProject.buttonProps} disabled={!valuesOk} className="ml-auto">
          <FolderTreeIcon />

          {t('create')}
        </Button>
      </ResponsiveOverlay>
    </>
  );
}
